import ConfirmationDialog from '@/components/Base/modals/ConfirmationDialog'
import { AxiomReportType } from '@/enums/axiom-report.enum'
import { IScheduledReportsDTO2 } from '@/interfaces/scenarioAnalysisReportComponents.interface'
import {
  getGridThemeClass,
  sideBarDefaultConfig,
  themeClassName,
} from '@/utils/grid-utils/ag-grid-config'
import { defaultColDefs } from '@/utils/grid-utils/colDefs'
import {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
} from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import MenuIcon from '@mui/icons-material/Menu'
import { IconButton, Menu, MenuItem } from '@mui/material'
import {
  customDateComparator,
  getUpdatedColumnsWithTooltip,
} from '@utils/tableGridUtils'
import React from 'react'

const formatFrequency = (frequency: number | null | undefined): string => {
  switch (frequency) {
    case 1:
      return 'Yearly'
    case 2:
      return 'Monthly'
    case 3:
      return 'Weekly'
    case 4:
      return 'Daily'
    case 5:
      return 'Hourly'
    default:
      return ''
  }
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

const formatUtcIsoToLocal = (value: string | null | undefined): string => {
  if (!value) return ''

  // Accept strings like:
  // - 2026-03-02T23:00:17.394623
  // - 2026-03-02T23:00:17.394Z
  // - 2026-03-02T23:00:17Z
  // Normalize fractional seconds to milliseconds and ensure UTC (Z).
  const match = value.match(
    /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:\d{2})?$/,
  )

  if (!match) return value

  const base = match[1]
  const fraction = match[2]
  const tz = match[3]

  const ms = fraction ? fraction.slice(0, 3).padEnd(3, '0') : '000'
  const normalized = `${base}.${ms}${tz ?? 'Z'}`

  const d = new Date(normalized)
  if (Number.isNaN(d.getTime())) return value

  const year = d.getFullYear()
  const month = pad2(d.getMonth() + 1)
  const day = pad2(d.getDate())
  const hour = pad2(d.getHours())
  const minute = pad2(d.getMinutes())
  const second = pad2(d.getSeconds())

  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}

export type IScheduledReportsList = {
  rowData: IScheduledReportsDTO2[]
  onDelete: (scheduleId: number) => void
  isDarkMode?: boolean
}

const ScheduledReportsList: React.FC<IScheduledReportsList> = ({
  rowData,
  onDelete,
  isDarkMode = false,
}) => {
  const columnDefs: ColDef[] = [
    {
      headerName: '',
      field: 'actions',
      cellRenderer: (params: ICellRendererParams<IScheduledReportsDTO2>) => {
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
        const [isDeleteDialogOpen, setIsDeleteDialogOpen] =
          React.useState(false)
        const open = Boolean(anchorEl)

        return (
          <>
            <IconButton
              id='action-button'
              aria-controls={open ? 'action-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
              ref={(ref: any) => {
                if (!ref) return
                ref.onclick = (e: MouseEvent) => {
                  e.stopPropagation()
                  setAnchorEl(ref)
                }
              }}
            >
              <MenuIcon fontSize='small' sx={{ fontSize: '16px' }} />
            </IconButton>
            <Menu
              id='action-menu'
              anchorEl={anchorEl}
              open={open}
              onClose={() => setAnchorEl(null)}
              MenuListProps={{ 'aria-labelledby': 'action-button' }}
            >
              <MenuItem
                onClick={() => {
                  setIsDeleteDialogOpen(true)
                  setAnchorEl(null)
                }}
              >
                Delete Scheduled Report
              </MenuItem>
            </Menu>
            <ConfirmationDialog
              open={isDeleteDialogOpen}
              title='Delete Scheduled Report'
              contentText='Are you sure you want to delete this scheduled report?'
              onConfirm={() => {
                if (onDelete && params.data?.id) onDelete(params.data.id)
                setIsDeleteDialogOpen(false)
              }}
              onCancel={() => setIsDeleteDialogOpen(false)}
            />
          </>
        )
      },
      suppressSizeToFit: true,
      width: 50,
      sortable: false,
      filter: false,
    },
    {
      headerName: 'Report Type',
      field: 'report_type',
    },
    {
      headerName: 'Template',
      field: 'template_name',
      // GFR/GFPA/MENU/RV reports use a hard-coded template_id (POSTGRES, or for
      // RV one of several predefined templates); when the backend returns a
      // null template name for them, show the template_id instead.
      valueFormatter: (params: any) => {
        const reportType = params.data?.report_type
        const templateName = params.value
        const isNullTemplate =
          templateName === null ||
          templateName === undefined ||
          templateName === ''
        if (
          isNullTemplate &&
          (reportType === AxiomReportType.GFR ||
            reportType === AxiomReportType.GFPA ||
            reportType === AxiomReportType.MENU ||
            reportType === AxiomReportType.RV)
        ) {
          return params.data?.template_id ?? ''
        }
        return templateName ?? ''
      },
    },
    {
      headerName: 'Portfolio',
      field: 'portfolio_name',
    },
    {
      headerName: 'Benchmark',
      field: 'benchmark',
    },
    {
      headerName: 'Frequency',
      field: 'frequency',
      valueFormatter: (params: any) => formatFrequency(params.value),
    },
    {
      headerName: 'Last Run',
      field: 'last_start_datetime',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: customDateComparator,
        buttons: ['reset'],
      },
      cellRenderer: (params: any) => {
        return (
          <span>
            {' '}
            {params.data.last_start_datetime === null ||
            params.data.last_start_datetime === '0001-01-01T00:00:00'
              ? 'No Last Run'
              : formatUtcIsoToLocal(params.data.last_start_datetime)}
          </span>
        )
      },
    },
    {
      headerName: 'Next Run',
      field: 'next_run_datetime',
      filter: 'agDateColumnFilter',
      filterParams: {
        comparator: customDateComparator,
        buttons: ['reset'],
      },
      cellRenderer: (params: any) => {
        return (
          <span>
            {' '}
            {params.data.next_run_datetime === null ||
            params.data.next_run_datetime === '0001-01-01T00:00:00'
              ? 'No Next Run'
              : formatUtcIsoToLocal(params.data.next_run_datetime)}
          </span>
        )
      },
    },
    {
      headerName: 'Status',
      field: 'status_text',
    },
  ]

  const onGridReady = (params: GridReadyEvent) => {
    if (document.body.clientWidth > 1400) {
      params.api.sizeColumnsToFit()
    } else {
      params.api.autoSizeAllColumns()
    }
  }

  return (
    <div
      className={`${isDarkMode ? getGridThemeClass(true) : themeClassName} schedule-scenario-table`}
      data-cy='scheduled-reports-ag-grid'
    >
      <AgGridReact
        onGridReady={onGridReady}
        defaultColDef={{ ...defaultColDefs, cellStyle: { textAlign: 'left' } }}
        columnDefs={getUpdatedColumnsWithTooltip(columnDefs)}
        rowData={rowData}
        sideBar={sideBarDefaultConfig}
      ></AgGridReact>
    </div>
  )
}

export default ScheduledReportsList
